'use client';

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { NavLink } from './NavLink';

interface AccordionLink {
  readonly label: string;
  readonly href: string;
}

interface AccordionCategory {
  readonly heading: string;
  readonly links: readonly AccordionLink[];
}

interface FooterAccordionProps {
  /** Visible title shown on the trigger row */
  title: string;
  /** Grouped categories of links shown when expanded */
  categories: readonly AccordionCategory[];
}

/**
 * Expandable accordion row for footer link groups.
 * Shows a title with +/- icon; expands to reveal links in a 4-column grid
 * grouped by category headings.
 */
export function FooterAccordion({ title, categories }: FooterAccordionProps) {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <div className="border-b border-border/20">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        className="flex w-full items-center justify-between py-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background lg:py-5"
      >
        <span className="text-[20px] font-light tracking-[-0.02em] text-foreground lg:text-[24px]">
          {title}
        </span>
        <span className="flex size-10 shrink-0 items-center justify-center rounded-full border border-border/30 lg:size-12">
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            className="text-foreground transition-transform duration-300"
            aria-hidden="true"
          >
            {isOpen ? (
              <path d="M4 10h12" />
            ) : (
              <>
                <path d="M10 4v12" />
                <path d="M4 10h12" />
              </>
            )}
          </svg>
        </span>
      </button>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="flex flex-col gap-6 pb-5 lg:pb-8">
              {categories.map((category) => (
                <div key={category.heading} className="flex flex-col gap-5">
                  {/* A single category that just repeats the trigger title
                      would double-label the group (e.g. "Agency" / AGENCY). */}
                  {!(
                    categories.length === 1 &&
                    category.heading.toLowerCase() === title.toLowerCase()
                  ) && (
                    <p className="text-[13px] font-normal uppercase tracking-[0.1em] text-muted">
                      {category.heading}
                    </p>
                  )}
                  <ul className="grid grid-cols-2 gap-x-8 gap-y-3 sm:grid-cols-3 lg:grid-cols-4">
                    {category.links.map((link) => (
                      <li key={link.label}>
                        <NavLink
                          href={link.href}
                          data-analytics-cta="footer_link"
                          data-analytics-location="footer_accordion"
                          data-analytics-label={link.label}
                          className="inline-flex min-h-6 items-center text-[15px] font-light text-muted transition-colors hover:text-foreground lg:text-[16px]"
                          activeClassName="text-foreground/60 hover:text-foreground/60"
                        >
                          {link.label}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
